/**
 * Small building blocks of the Salvation Team console: the capability gate, the page header (with the page's one ?
 * help button), the status and lookup badges, the person flags, a text link for rows and cards, a drawer section and
 * the Lead / Backup cards (who is responsible now is highlighted; ownership never changes here).
 */

import type { ReactNode } from 'react';
import { IconLock, IconAlertTriangle, IconUserCheck } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { EmptyState } from '@/components/ui/data-display';
import { Anchor, Badge, Box, Group, Paper, Stack, Text, Title, Tooltip } from '@/components/ui';
import { PageHelpButton } from '@/components/help';
import type { SalvationPersonRef, SalvationStatusItem } from '@actschurch/ccms-sdk';
import { useSalvationPermissions, type SalvationPermissions } from '@/hooks/salvation/useSalvationPermissions';
import { badgeColour } from './salvationUtils';

const badgeStyles = { root: { textTransform: 'none' as const } };

type Size = 'xs' | 'sm' | 'md' | 'lg';

// ---------------------------------------------------------------- gate

interface SalvationGateProps {
  allow: (p: SalvationPermissions) => boolean;
  children: ReactNode;
}

/** Capability gate for Salvation pages (role OR right, like the API). */
export function SalvationGate({ allow, children }: SalvationGateProps) {
  const perms = useSalvationPermissions();
  if (!allow(perms)) {
    return (
      <LayoutContainer>
        <EmptyState
          icon={<IconLock size={40} />}
          title="Access denied"
          description="You don't have permission to open this page. Ask a Salvation Team admin for access."
        />
      </LayoutContainer>
    );
  }
  return <>{children}</>;
}

// ---------------------------------------------------------------- header

interface SalvationPageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** The page's help key ("salvation.people"): the ? button and the "{helpKey}.actions" tour anchor. */
  helpKey: string;
  actions?: ReactNode;
  /** Shown above the title (for example a back link). */
  above?: ReactNode;
  /** Shown under the title (for example status badges). */
  below?: ReactNode;
}

/**
 * Page header: title and one-line description on the left, the page actions (anchored as "{helpKey}.actions" for
 * tours) and the ? help button on the right. The same shape as the Call Centre console header.
 */
export function SalvationPageHeader({ title, description, helpKey, actions, above, below }: SalvationPageHeaderProps) {
  return (
    <Group justify="space-between" wrap="wrap" align="flex-start" gap="sm">
      <div style={{ minWidth: 0, flex: 1 }}>
        {above}
        <Title order={2}>{title}</Title>
        {description && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}
        {below}
      </div>
      <Group gap="sm" data-help-id={`${helpKey}.actions`} wrap="wrap">
        {actions}
        <PageHelpButton pageKey={helpKey} />
      </Group>
    </Group>
  );
}

// ---------------------------------------------------------------- badges

/** A lookup name in its colour (names and colours come from the API's lookup tables). */
export function LookupBadge({ name, color, size = 'sm', fallback = 'gray', variant = 'light', leftSection }: {
  name: ReactNode;
  color?: string | null;
  size?: Size;
  fallback?: string;
  variant?: 'light' | 'filled' | 'outline' | 'dot';
  leftSection?: ReactNode;
}) {
  return (
    <Badge size={size} variant={variant} color={badgeColour(color, fallback)} styles={badgeStyles} leftSection={leftSection}>
      {name}
    </Badge>
  );
}

/** The person's status in the lookup colour (falls back to the id when lookups are still loading). */
export function StatusBadge({ statusId, statuses, size = 'sm' }: { statusId: number; statuses: Map<number, SalvationStatusItem>; size?: Size }) {
  const s = statuses.get(statusId);
  return (
    <Badge size={size} variant="light" color={badgeColour(s?.color)} styles={{ root: { textTransform: 'none', whiteSpace: 'nowrap' } }}>
      {s?.name ?? `Status ${statusId}`}
    </Badge>
  );
}

/** "Past the deadline" / "Backup responsible" / "Needs review" flags of a person. */
export function PersonFlags({ isOverdue, backupIsResponsible, reviewNeeded, isEscalated }: {
  isOverdue?: boolean;
  backupIsResponsible?: boolean;
  reviewNeeded?: boolean;
  isEscalated?: boolean;
}) {
  if (!isOverdue && !backupIsResponsible && !reviewNeeded && !isEscalated) return null;
  return (
    <Group gap={4} wrap="wrap">
      {isEscalated && <Badge size="xs" color="red" variant="filled" styles={badgeStyles}>Escalated</Badge>}
      {isOverdue && !isEscalated && <Badge size="xs" color="red" variant="light" styles={badgeStyles}>Past the deadline</Badge>}
      {backupIsResponsible && <Badge size="xs" color="orange" variant="light" styles={badgeStyles}>Backup responsible</Badge>}
      {reviewNeeded && <Badge size="xs" color="grape" variant="light" styles={badgeStyles}>Needs review</Badge>}
    </Group>
  );
}

// ---------------------------------------------------------------- links and sections

/**
 * InlineLink: a text link inside rows and cards. The themed Anchor takes no click handler, so the click is caught on
 * a Box around it: with `onClick` it acts in the app, with only `href` it follows the link (tel:, mailto:). It never
 * lets the click reach a clickable row underneath.
 */
export function InlineLink({ children, onClick, href, target, size = 'sm' }: {
  children: ReactNode;
  onClick?: () => void;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  size?: 'xs' | 'sm' | 'md';
}) {
  return (
    <Box
      style={{ display: 'inline-block', cursor: 'pointer' }}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (onClick && !(e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Anchor size={size} href={href} target={target} rel={target === '_blank' ? 'noopener noreferrer' : undefined}>
        {children}
      </Anchor>
    </Box>
  );
}

/** A titled block inside a drawer (icon, title and a count badge). */
export function DrawerSection({ title, icon, count, actions, children }: { title: string; icon: ReactNode; count?: number; actions?: ReactNode; children: ReactNode }) {
  return (
    <Stack gap="xs">
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Group gap={6} wrap="nowrap">
          {icon}
          <Text fw={600} size="sm">
            {title}
          </Text>
          {count != null && count > 0 && (
            <Badge size="xs" variant="light" color="gray" styles={badgeStyles}>
              {count}
            </Badge>
          )}
        </Group>
        {actions}
      </Group>
      {children}
    </Stack>
  );
}

// ---------------------------------------------------------------- Lead / Backup

function RoleCard({ label, person, responsible, reason }: { label: string; person?: SalvationPersonRef | null; responsible: boolean; reason?: string }) {
  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      style={{
        flex: 1,
        minWidth: 180,
        borderColor: responsible ? 'var(--mantine-color-green-6)' : undefined,
        borderWidth: responsible ? 2 : undefined,
      }}
    >
      <Stack gap={2}>
        <Group gap={6} justify="space-between" wrap="nowrap">
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            {label}
          </Text>
          {responsible && (
            <Tooltip label={reason ?? 'Responsible now'} withArrow>
              <Badge size="xs" color="green" variant="light" leftSection={<IconUserCheck size={12} />} styles={badgeStyles}>
                Responsible now
              </Badge>
            </Tooltip>
          )}
        </Group>
        {person ? (
          <Group gap={6} wrap="nowrap">
            <Text fw={600}>{person.displayName}</Text>
            {!person.isActive && (
              <Tooltip label="No longer active on the team" withArrow>
                <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" />
              </Tooltip>
            )}
          </Group>
        ) : (
          <Text c="dimmed" fs="italic">
            Not assigned yet
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

/** The permanent Lead and Backup, with "responsible now" on whoever should act. */
export function LeadBackupCards({ lead, backup, responsible, reason }: {
  lead?: SalvationPersonRef | null;
  backup?: SalvationPersonRef | null;
  responsible?: SalvationPersonRef | null;
  reason?: string;
}) {
  const responsibleId = responsible?.teamMemberID;
  return (
    <Group gap="sm" align="stretch" wrap="wrap">
      <RoleCard label="Lead" person={lead} responsible={!!lead && lead.teamMemberID === responsibleId} reason={reason} />
      <RoleCard label="Backup" person={backup} responsible={!!backup && backup.teamMemberID === responsibleId} reason={reason} />
    </Group>
  );
}
